<template>
  <div>
    <!-- แถบควบคุม -->
    <div class="d-flex align-center flex-wrap ga-3 mb-3">
      <v-btn-toggle v-model="channel" density="comfortable" variant="outlined" divided mandatory rounded="lg" color="primary">
        <v-btn value="voice" size="small" prepend-icon="mdi-phone">{{ $t('qaVoice') }}</v-btn>
        <v-btn value="chat" size="small" prepend-icon="mdi-message-text">{{ $t('qaChat') }}</v-btn>
      </v-btn-toggle>

      <!-- เลขเวอร์ชันเป็นของฉบับ active เสมอ — ฉบับร่างยังใช้เลขเดิมจนกว่าจะ publish (server เป็นคนกำหนด) -->
      <v-chip color="primary" size="small" variant="tonal" label>v{{ activeVersion }}</v-chip>
      <v-chip v-if="isDefault" color="grey" size="small" variant="tonal" label>
        {{ $t('qaRubDefaultChip') }}
        <v-tooltip activator="parent" location="top">{{ $t('qaRubDefaultTip') }}</v-tooltip>
      </v-chip>
      <v-chip v-if="hasDraft" color="warning" size="small" variant="tonal" label>
        {{ $t('qaRubHasDraftChip') }}
        <v-tooltip activator="parent" location="top">{{ $t('qaRubHasDraftTip', { version: activeVersion }) }}</v-tooltip>
      </v-chip>

      <v-spacer />

      <v-card variant="outlined" class="d-flex align-center ga-3 px-4 py-2 qa-card-sm">
        <span class="text-caption text-medium-emphasis">{{ $t('qaRubTotalScore') }}</span>
        <span class="text-h6 font-weight-medium qa-num">{{ totalScore }}</span>
        <v-chip :color="totalColor" size="small" variant="tonal" label>{{ totalMessage }}</v-chip>
      </v-card>
    </div>

    <!-- หมวดและหัวข้อ -->
    <v-card v-for="(part, pi) in rubric.parts" :key="part.partId" class="qa-card-sm mb-3" variant="outlined">
      <div class="d-flex align-center ga-2 pa-3 qa-part-head">
        <v-avatar color="primary" size="24" rounded="lg">
          <span class="text-caption text-white">{{ pi + 1 }}</span>
        </v-avatar>
        <v-text-field v-model="part.titleTh" density="compact" variant="plain" hide-details class="qa-part-title" />
        <span class="text-caption text-medium-emphasis qa-num">{{ partTotal(part) }} {{ $t('qaRubPoints') }}</span>
        <v-btn icon size="small" variant="text" color="error" @click="removePart(pi)">
          <v-icon size="18">mdi-delete-outline</v-icon>
          <v-tooltip activator="parent" location="top">{{ $t('qaRubDeletePart') }}</v-tooltip>
        </v-btn>
      </div>
      <v-divider />

      <draggable v-model="part.criteria" item-key="criteriaId" handle=".qa-grip" animation="160">
        <template #item="{ element: crit, index: ci }">
          <div class="qa-crit-row">
            <v-icon class="qa-grip" size="18" color="grey-lighten-1">mdi-drag</v-icon>

            <v-text-field v-model="crit.nameTh" density="compact" variant="plain" hide-details class="qa-crit-name" />

            <div class="d-flex align-center ga-1">
              <v-text-field
                :model-value="crit.maxScore"
                density="compact"
                variant="outlined"
                hide-details
                type="number"
                class="qa-score-input"
                @update:model-value="setScore(crit, $event)"
              />
              <span class="text-caption text-medium-emphasis">{{ $t('qaRubPoints') }}</span>
            </div>

            <div class="d-flex ga-1 qa-crit-flags">
              <v-chip v-if="crit.requireEvidence" size="x-small" color="indigo" variant="tonal" label>{{ $t('qaRubEvidenceChip') }}</v-chip>
              <v-chip v-if="crit.isAutoFail" size="x-small" color="error" variant="tonal" label>Auto-fail</v-chip>
              <v-chip v-if="crit.allowNotApplicable" size="x-small" color="teal" variant="tonal" label>{{ $t('qaRubNaChip') }}</v-chip>
              <v-chip v-if="!crit.isActive" size="x-small" color="grey" variant="tonal" label>{{ $t('qaRubDisabledChip') }}</v-chip>
            </div>

            <div class="d-flex ga-1">
              <v-btn icon size="small" variant="text" @click="editCriteria(pi, ci)">
                <v-icon size="18">mdi-pencil-outline</v-icon>
                <v-tooltip activator="parent" location="top">{{ $t('qaRubEditPromptTip') }}</v-tooltip>
              </v-btn>
              <v-btn icon size="small" variant="text" color="error" @click="removeCriteria(pi, ci)">
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
      </draggable>

      <div class="pa-3">
        <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addCriteria(pi)">{{ $t('qaRubAddCriteria') }}</v-btn>
      </div>
    </v-card>

    <div class="d-flex align-center ga-2 flex-wrap mb-2">
      <v-btn variant="tonal" color="primary" prepend-icon="mdi-plus" @click="addPart">{{ $t('qaRubAddPart') }}</v-btn>
      <v-btn variant="tonal" prepend-icon="mdi-restore" @click="load">{{ $t('qaRubDiscard') }}</v-btn>
      <v-spacer />
      <v-btn variant="tonal" :loading="saving" @click="save">{{ $t('qaRubSaveDraft') }}</v-btn>
      <!-- publish เป็นสิทธิ์ role 0 เท่านั้น (APIAuthen แถว /publish) — ซ่อนปุ่มไปเลย
           ดีกว่าปล่อยให้กดแล้วเจอ 401 ที่อธิบายอะไรไม่ได้ -->
      <v-btn v-if="canPublish" color="primary" variant="flat" :loading="publishing" prepend-icon="mdi-check" @click="publish">Publish</v-btn>
    </div>
    <div class="text-caption text-medium-emphasis mb-5">
      {{ $t('qaRubDraftNote') }}
      <template v-if="updatedAt"> · {{ $t('qaRubLastEditedBy') }} {{ updatedBy || $t('qaRubUnknown') }} · {{ updatedAtText }}</template>
      <template v-else> · {{ $t('qaRubNeverSaved') }}</template>
    </div>

    <!-- Compliance gates -->
    <v-card class="qa-card-sm" variant="outlined">
      <v-card-title class="d-flex align-center py-3">
        <v-icon size="18" color="indigo" class="mr-2">mdi-shield-check-outline</v-icon>
        <span class="text-subtitle-1">{{ $t('qaComplianceGates') }}</span>
        <v-spacer />
        <v-chip color="grey" size="small" variant="tonal" label>{{ $t('qaRubGatesOffChip') }}</v-chip>
      </v-card-title>
      <v-divider />
      <!-- มติ 24 ส.ค. 2026: gates ยังไม่ถูกส่งเข้า LLM — เก็บช่องแก้ไว้เพื่อให้เตรียม prompt
           ล่วงหน้าได้ แต่ต้องประกาศตรง ๆ ว่ายังไม่มีผล ไม่งั้นคนจะเข้าใจว่าเคสถูกตรวจ gate อยู่ -->
      <v-alert type="info" variant="tonal" density="compact" class="ma-3 mb-0">
        <span class="text-body-2">
          {{ $t('qaRubGatesOffNote') }}
        </span>
      </v-alert>
      <div v-for="(gate, gi) in rubric.gates" :key="gate.gateId" class="qa-gate-row">
        <v-avatar color="indigo" variant="tonal" size="30" rounded="lg">
          <v-icon size="16" color="indigo">mdi-shield-outline</v-icon>
        </v-avatar>
        <div class="flex-grow-1" style="min-width: 0">
          <div class="text-body-2 font-weight-medium">{{ gate.name }}</div>
          <div class="text-caption text-medium-emphasis text-truncate">{{ firstLine(gate.prompt) }}</div>
        </div>
        <v-chip color="error" size="small" variant="tonal" label>{{ gate.severity }}</v-chip>
        <div class="d-flex align-center ga-2">
          <span class="text-caption text-medium-emphasis d-none d-sm-inline">Hard-fail</span>
          <v-switch v-model="gate.isHardFail" color="primary" density="compact" hide-details inset />
        </div>
        <v-btn icon size="small" variant="text" @click="editGate(gi)">
          <v-icon size="18">mdi-pencil-outline</v-icon>
          <v-tooltip activator="parent" location="top">{{ $t('qaRubEditGateTip') }}</v-tooltip>
        </v-btn>
      </div>
    </v-card>

    <!-- ตัวแก้ไข gate — แก้แล้วต้องกด "บันทึกเป็นฉบับร่าง" เหมือนหัวข้อปกติ -->
    <v-dialog v-model="gateDialog" max-width="640" scrollable>
      <v-card v-if="gateDraft">
        <v-card-title class="d-flex align-center py-3">
          <span class="text-subtitle-1">{{ gateDraft.name || 'Compliance gate' }}</span>
          <v-spacer />
          <v-btn icon size="small" variant="text" @click="gateDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="8"><v-text-field v-model="gateDraft.name" :label="$t('qaRubGateName')" density="compact" variant="outlined" hide-details /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="gateDraft.severity" label="Severity" density="compact" variant="outlined" hide-details /></v-col>
          </v-row>
          <div class="qa-section-label">{{ $t('qaRubGatePromptLabel') }}</div>
          <v-textarea v-model="gateDraft.prompt" variant="outlined" rows="6" hide-details auto-grow spellcheck="false" />
          <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaRubGatePromptNote') }}</div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn color="primary" variant="flat" @click="applyGateDraft">{{ $t('qaRubSaveGate') }}</v-btn>
          <v-btn variant="text" @click="gateDialog = false">{{ $t('qaRubCancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ตัวแก้ไขหัวข้อ -->
    <v-dialog v-model="dialog" max-width="720" scrollable>
      <v-card v-if="draft">
        <v-card-title class="d-flex align-center py-3">
          <div>
            <div class="text-subtitle-1">{{ draft.nameTh }}</div>
            <div class="text-caption text-medium-emphasis">{{ dialogSubtitle }}</div>
          </div>
          <v-spacer />
          <v-btn icon size="small" variant="text" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <div class="qa-section-label">{{ $t('qaRubBasicInfo') }}</div>
          <v-row dense>
            <v-col cols="12" sm="6"><v-text-field v-model="draft.nameTh" :label="$t('qaRubNameTh')" density="compact" variant="outlined" hide-details /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="draft.nameEn" :label="$t('qaRubNameEn')" density="compact" variant="outlined" hide-details /></v-col>
            <v-col cols="6" sm="4"><v-text-field v-model.number="draft.maxScore" :label="$t('qaRubMaxScore')" type="number" density="compact" variant="outlined" hide-details /></v-col>
            <v-col cols="6" sm="8">
              <v-select v-model="draft.scoreType" :items="scoreTypeItems" :label="$t('qaRubScoreType')" density="compact" variant="outlined" hide-details />
            </v-col>
          </v-row>

          <div class="qa-section-label">{{ $t('qaRubPromptLabel') }}</div>
          <v-textarea v-model="draft.prompt" variant="outlined" rows="8" hide-details auto-grow spellcheck="false" />
          <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaRubPromptNote') }}</div>

          <div class="qa-section-label">{{ $t('qaRubAnchorsLabel') }}</div>
          <v-table density="compact" class="qa-anchor-table">
            <thead>
              <tr>
                <th style="width: 96px">{{ $t('qaRubPoints') }}</th>
                <th>{{ $t('qaRubAnchorMeaning') }}</th>
                <th style="width: 46px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(anchor, i) in draft.anchors" :key="i">
                <td><v-text-field v-model="anchor.label" density="compact" variant="plain" hide-details class="qa-num" /></td>
                <td><v-text-field v-model="anchor.description" density="compact" variant="plain" hide-details /></td>
                <td>
                  <v-btn icon size="x-small" variant="text" color="error" @click="draft.anchors.splice(i, 1)"><v-icon size="16">mdi-close</v-icon></v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" class="mt-2" @click="draft.anchors.push({ label: '', description: '' })">{{ $t('qaRubAddAnchor') }}</v-btn>
          <div class="text-caption text-medium-emphasis mt-2">{{ $t('qaRubAnchorsNote') }}</div>

          <div class="qa-section-label">{{ $t('qaRubMoreOptions') }}</div>
          <v-switch v-model="draft.requireEvidence" color="primary" density="compact" hide-details inset :label="$t('qaRubRequireEvidence')" />
          <v-switch v-model="draft.isAutoFail" color="error" density="compact" hide-details inset :label="$t('qaRubAutoFail')" />
          <v-switch v-model="draft.allowNotApplicable" color="teal" density="compact" hide-details inset :label="$t('qaRubAllowNa')" />
          <!-- ⚠️ ต้องเตือนตรงนี้ ไม่ใช่ใน docs — คนเปิด N-A ทุกหัวข้อคือคนที่ไม่ได้อ่าน docs -->
          <div class="text-caption text-medium-emphasis ml-2 mb-2">
            {{ $t('qaRubAllowNaNote') }}
          </div>
          <v-switch v-model="draft.isActive" color="primary" density="compact" hide-details inset :label="$t('qaRubIsActive')" />

          <div class="qa-section-label">{{ $t('qaRubTestSection') }}</div>
          <!-- ทดลอง = 1 LLM call จริงกับ transcript จริง — ต้องระบุเคสที่ปิดแล้ว ไม่มีบทสนทนาตัวอย่างในระบบอีกแล้ว -->
          <div class="d-flex align-center ga-2 flex-wrap">
            <v-text-field
              v-model="testCaseKey"
              :label="$t('qaRubTestCaseLabel')"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 220px"
              @keyup.enter="runTest"
            />
            <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-play" :loading="testing" :disabled="!testCaseKey.trim()" @click="runTest">{{ $t('qaRubRunTest') }}</v-btn>
            <span class="text-caption text-medium-emphasis">{{ $t('qaRubTestNote') }}</span>
          </div>

          <v-alert v-if="testError" type="error" variant="tonal" density="compact" class="mt-3">
            <span class="text-body-2">{{ testError }}</span>
          </v-alert>
          <v-card v-if="testResult" variant="outlined" class="qa-card-sm mt-3">
            <div class="pa-3">
              <div class="d-flex align-center ga-2 flex-wrap mb-2">
                <span class="text-h6 qa-num">{{ testResult.earned ?? '—' }} / {{ testResult.max }}</span>
                <v-chip v-if="testResult.confidence != null" size="small" variant="tonal" :color="testResult.confidence >= 0.7 ? 'success' : 'warning'" label>
                  {{ $t('qaRubConfidence') }} {{ Math.round(testResult.confidence * 100) }}%
                </v-chip>
                <v-chip v-if="testResult.needsHumanReview" size="small" color="warning" variant="tonal" label>{{ $t('qaRubNeedsReview') }}</v-chip>
                <v-spacer />
                <span class="text-caption text-medium-emphasis qa-num">{{ testResult.lineCount }} {{ $t('qaRubLines') }}</span>
              </div>
              <div v-if="testResult.reason" class="text-body-2 mb-1">{{ testResult.reason }}</div>
              <div v-if="testResult.evidence && testResult.evidence.quote" class="text-caption text-medium-emphasis">
                {{ $t('qaRubEvidenceLabel') }}: “{{ testResult.evidence.quote }}”
              </div>
            </div>
          </v-card>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn color="primary" variant="flat" @click="applyDraft">{{ $t('qaRubSaveCriteria') }}</v-btn>
          <v-btn variant="text" @click="dialog = false">{{ $t('qaRubCancel') }}</v-btn>
          <v-spacer />
          <span class="text-caption text-medium-emphasis d-none d-sm-inline">{{ $t('qaRubDraftUntilPublish') }}</span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Rubric Builder — หัวใจของหน้า Settings (Q9: ของจริงแล้ว ไม่ใช่ localStorage)
 *
 * โมเดลการทำงานตาม backend (`QaEvaluationBAL`):
 *   - GET settings/rubric/{channel} คืน **ฉบับร่างถ้ามี ไม่งั้นฉบับ active** พร้อมธง
 *     hasDraft / isDefault และเลขเวอร์ชันของ active (draft ไม่มีเลขของตัวเอง)
 *   - save = เขียน**ฉบับร่าง**ทับ — ไม่บังคับรวม 100 เก็บงานค้างครึ่งทางได้
 *   - publish = server ตรวจ (รวม 100 · prompt ครบ · id ไม่ซ้ำ) แล้วเลื่อนเป็น active v+1
 *     ตกข้อไหนได้ 400 พร้อมข้อความไทย — หน้าจอ**ไม่เช็คเอง** จะได้ไม่มีกติกาสองชุดเถียงกัน
 *
 * ⚠️ hasScoredCases มาจาก server เท่านั้น (มีแถวคะแนนอ้างถึง criteriaId แล้ว = ลบไม่ได้)
 *    ค่าที่หน้าจอส่งไปตอน save ถูก server ทิ้งเสมอ — ห้ามคำนวณเองฝั่งนี้
 */
import draggable from 'vuedraggable';
import qaConsole from '@/services/qaConsole';

export default {
  name: 'QaRubricBuilder',
  components: { draggable },
  emits: ['saved'],
  // roleId มาจาก App.vue (Cookies.get("roleId") · provide ผ่าน toRef) — แบบเดียวกับ MutualFund.vue
  inject: { RoleId: { default: null } },
  data() {
    return {
      // เคสจริงทั้งระบบเป็นแชท — เปิดมาต้องเจอ rubric ที่ถูกใช้ให้คะแนนจริงก่อน ไม่ใช่ voice ที่ยังไม่มีเคส
      channel: 'chat',
      rubric: { parts: [], gates: [] },
      activeVersion: 1,
      hasDraft: false,
      isDefault: true,
      updatedAt: null,
      updatedBy: null,
      saving: false,
      publishing: false,
      dialog: false,
      draft: null,
      draftIndex: { part: 0, criteria: 0 },
      gateDialog: false,
      gateDraft: null,
      gateDraftIndex: 0,
      testing: false,
      testCaseKey: '',
      testResult: null,
      testError: '',
      scoreTypeItems: [
        { title: this.$t('qaRubScoreTypeNumeric'), value: 'numeric' },
        { title: this.$t('qaRubScoreTypeScale'), value: 'scale' },
        { title: this.$t('qaRubScoreTypeBoolean'), value: 'boolean' },
      ],
    };
  },
  computed: {
    // นับเฉพาะหัวข้อที่เปิดใช้งาน — กติกาเดียวกับ ValidateForPublish ฝั่ง server
    // (หัวข้อที่ปิดชั่วคราวไม่ถูกส่งเข้า LLM จึงต้องไม่ถูกนับใน 100)
    totalScore() {
      return this.rubric.parts.reduce((sum, part) => sum + this.partTotal(part), 0);
    },
    totalColor() {
      if (this.totalScore === 100) return 'success';
      return this.totalScore < 100 ? 'warning' : 'error';
    },
    totalMessage() {
      if (this.totalScore === 100) return this.$t('qaRubTotalExact');
      return this.totalScore < 100
        ? this.$t('qaRubTotalShort', { n: 100 - this.totalScore })
        : this.$t('qaRubTotalOver', { n: this.totalScore - 100 });
    },
    // publish จำกัด role 0 ที่ APIAuthen — ซ่อนปุ่มตามสิทธิ์จริง ไม่ใช่เดาจากหน้าจอ
    canPublish() {
      return String(this.RoleId ?? '') === '0';
    },
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
    dialogSubtitle() {
      const part = this.rubric.parts[this.draftIndex.part];
      return part ? `${this.channel === 'voice' ? 'Voice' : 'Chat'} · ${this.$t('qaRubPartWord')} ${this.draftIndex.part + 1} ${part.titleTh}` : '';
    },
  },
  watch: {
    // เปลี่ยน channel = โหลดจาก server ใหม่ — ไม่มี cache ฝั่งนี้
    // การแก้ที่ยังไม่กดบันทึกจะหายไปโดยตั้งใจ (พฤติกรรมเดียวกับ "ยกเลิกการแก้ไข")
    channel() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const res = await qaConsole.getRubric(this.channel);
        // ไม่ clone — res เป็น object ใหม่จาก server ทุกครั้ง แก้บนนี้ได้ตรง ๆ
        // "ยกเลิกการแก้ไข" คือการเรียก load() ซ้ำ ไม่ใช่ย้อนจากสำเนาในเครื่อง
        this.rubric = res && res.rubric ? res.rubric : { parts: [], gates: [] };
        this.activeVersion = res ? res.activeVersion : 1;
        this.hasDraft = !!(res && res.hasDraft);
        this.isDefault = !!(res && res.isDefault);
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaRubLoadFailed', { msg: err.message }) });
      }
    },
    partTotal(part) {
      return part.criteria.reduce((sum, c) => sum + (c.isActive === false ? 0 : Number(c.maxScore) || 0), 0);
    },
    firstLine(text) {
      return String(text || '').split('\n')[0];
    },
    setScore(crit, value) {
      let n = parseFloat(value);
      if (Number.isNaN(n) || n < 0) n = 0;
      if (n > 100) n = 100;
      // ขั้นละ 0.5 ให้ตรงกับ Normalized() ฝั่ง server — หน้าจอต้องไม่แสดงค่าที่ server จะปัดทิ้ง
      crit.maxScore = Math.round(n * 2) / 2;
    },
    addPart() {
      this.rubric.parts.push({ partId: `part-${Date.now()}`, partNo: this.rubric.parts.length + 1, titleTh: this.$t('qaRubNewPart'), titleEn: 'New part', sortOrder: this.rubric.parts.length + 1, criteria: [] });
    },
    removePart(pi) {
      const part = this.rubric.parts[pi];
      // หมวดที่มีหัวข้อเคยให้คะแนนแล้ว ลบทั้งหมวดไม่ได้ด้วยเหตุผลเดียวกับ removeCriteria
      if (part.criteria.some((c) => c.hasScoredCases)) {
        window.alert(this.$t('qaRubPartHasScores', { name: part.titleTh }));
        return;
      }
      if (!window.confirm(this.$t('qaRubConfirmDeletePart', { name: part.titleTh }))) return;
      this.rubric.parts.splice(pi, 1);
    },
    addCriteria(pi) {
      this.rubric.parts[pi].criteria.push({
        criteriaId: `crit-${Date.now()}`,
        nameTh: this.$t('qaRubNewCriteria'),
        nameEn: 'New criteria',
        maxScore: 5,
        scoreType: 'numeric',
        prompt: this.$t('qaRubNewCriteriaPrompt'),
        anchors: [
          { label: this.$t('qaRubAnchorFull'), description: this.$t('qaRubAnchorFullDesc') },
          { label: this.$t('qaRubAnchorMid'), description: this.$t('qaRubAnchorMidDesc') },
          { label: '0', description: this.$t('qaRubAnchorZeroDesc') },
        ],
        requireEvidence: true,
        isAutoFail: false,
        allowNotApplicable: false,
        isActive: true,
        sortOrder: this.rubric.parts[pi].criteria.length + 1,
        hasScoredCases: false,
      });
    },
    removeCriteria(pi, ci) {
      const crit = this.rubric.parts[pi].criteria[ci];
      // หัวข้อที่เคยใช้ให้คะแนนแล้ว (server เป็นคนบอกผ่าน hasScoredCases) ห้ามลบ —
      // ไม่งั้นคะแนนย้อนหลังจะอ่านไม่ออก · เสนอ "ปิดใช้งาน" แทน
      if (crit.hasScoredCases) {
        if (!window.confirm(this.$t('qaRubCritHasScores', { name: crit.nameTh }))) return;
        crit.isActive = false;
        return;
      }
      if (!window.confirm(this.$t('qaRubConfirmDeleteCrit', { name: crit.nameTh }))) return;
      this.rubric.parts[pi].criteria.splice(ci, 1);
    },
    editCriteria(pi, ci) {
      this.draftIndex = { part: pi, criteria: ci };
      this.draft = JSON.parse(JSON.stringify(this.rubric.parts[pi].criteria[ci]));
      this.testResult = null;
      this.testError = '';
      this.dialog = true;
    },
    applyDraft() {
      const { part, criteria } = this.draftIndex;
      this.draft.maxScore = Number(this.draft.maxScore) || 0;
      this.rubric.parts[part].criteria.splice(criteria, 1, JSON.parse(JSON.stringify(this.draft)));
      this.dialog = false;
    },
    editGate(gi) {
      this.gateDraftIndex = gi;
      this.gateDraft = JSON.parse(JSON.stringify(this.rubric.gates[gi]));
      this.gateDialog = true;
    },
    applyGateDraft() {
      this.rubric.gates.splice(this.gateDraftIndex, 1, JSON.parse(JSON.stringify(this.gateDraft)));
      this.gateDialog = false;
    },
    async runTest() {
      const caseKey = this.testCaseKey.trim();
      if (!caseKey) return;
      this.testing = true;
      this.testError = '';
      try {
        // ส่งหัวข้อทั้งก้อน (รวม anchors/scoreType) — backend ประกอบ rubric ชั่วคราว
        // หัวข้อเดียวแล้ววิ่งเส้นทางเดียวกับการประเมินจริงทั้งเส้น
        this.testResult = await qaConsole.testCriteriaPrompt(this.channel, { caseKey, criteria: this.draft });
      } catch (err) {
        this.testResult = null;
        // 400 = เคสไม่มี/ยังไม่ปิด — ข้อความจาก server บอกเองว่าต้องใช้เคสที่จบแล้ว
        this.testError = err.message;
      } finally {
        this.testing = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        // ส่งทั้ง view ได้เลย — server bind เฉพาะ { name, parts, gates } (QaRubricDocument)
        // ฟิลด์เกิน (rubricId/version/status) ถูกทิ้ง และเวอร์ชันเป็นของ server เสมอ
        const res = await qaConsole.saveRubric(this.channel, this.rubric);
        this.rubric = res && res.rubric ? res.rubric : this.rubric;
        this.activeVersion = res ? res.activeVersion : this.activeVersion;
        this.hasDraft = !!(res && res.hasDraft);
        this.isDefault = !!(res && res.isDefault);
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaRubSavedDraft') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaRubSaveDraftFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
    async publish() {
      // ⚠️ ไม่เช็ครวม 100 ฝั่งนี้ — server ตรวจครบกว่า (รวม 100 · prompt ครบ · id ซ้ำ)
      //    และคืน 400 พร้อมข้อความไทยที่แสดงได้ตรง ๆ · เช็คซ้ำสองที่ = กติกาสองชุดที่เถียงกันได้
      if (
        !window.confirm(
          this.$t('qaRubConfirmPublish', {
            channel: this.channel === 'voice' ? 'Voice' : 'Chat',
            version: this.activeVersion + 1,
          })
        )
      )
        return;

      this.publishing = true;
      try {
        // save ก่อนเสมอ — publish ฝั่ง server เอา "ฉบับร่างที่บันทึกไว้" ไม่ใช่สิ่งที่ค้างบนหน้าจอ
        // ถ้าไม่ save ก่อน สิ่งที่ publish จะเป็นร่างเก่า ไม่ใช่ที่ผู้ใช้เห็นอยู่
        await qaConsole.saveRubric(this.channel, this.rubric);
        const res = await qaConsole.publishRubric(this.channel);
        this.rubric = res && res.rubric ? res.rubric : this.rubric;
        this.activeVersion = res ? res.activeVersion : this.activeVersion;
        this.hasDraft = !!(res && res.hasDraft);
        this.isDefault = !!(res && res.isDefault);
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaRubPublished', { version: this.activeVersion }) });
      } catch (err) {
        // err.message คือข้อความ validation ไทยจาก 400 ของ server
        this.$emit('saved', { ok: false, message: err.message });
      } finally {
        this.publishing = false;
      }
    },
  },
};
</script>

<style scoped>
.qa-part-head {
  background: #fafbfc;
}
.qa-part-title :deep(input) {
  font-size: 14px;
  font-weight: 500;
}
.qa-crit-row {
  display: grid;
  grid-template-columns: 22px minmax(140px, 1fr) 132px minmax(120px, 160px) 84px;
  gap: 10px;
  align-items: center;
  padding: 4px 12px;
  border-bottom: 1px solid #f1f3f5;
}
.qa-crit-row:hover {
  background: #f7fafd;
}
.qa-grip {
  cursor: grab;
}
.qa-crit-name :deep(input) {
  font-size: 14px;
}
.qa-score-input {
  max-width: 78px;
}
.qa-score-input :deep(input) {
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.qa-gate-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f3f5;
}
.qa-gate-row:last-child {
  border-bottom: none;
}
.qa-section-label {
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #9aa1a9;
  font-weight: 600;
  margin: 22px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f3f5;
}
.qa-section-label:first-child {
  margin-top: 0;
}
.qa-anchor-table :deep(th) {
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7280;
}
@media (max-width: 800px) {
  .qa-crit-row {
    grid-template-columns: 22px 1fr 120px 84px;
  }
  .qa-crit-flags {
    display: none !important;
  }
}
</style>
